import { IRootNode } from "../Providers.Api/Models";
import { IAdminPortalRights, IApiRights, IDataModelRights, IFlexRights, IIBSSRights, IOneLens360Rights, IRoamerRights, IUserRights } from "../Providers.Api/Models.UserApi";
import { Space } from "../Providers.Api/Spaces/SpaceRepository";
import { IDefaultCostCodes, IUserPreferences } from "../Providers.Api/UserPreferences/UserPreferenceRepository";
import { IUserDetails } from "./Models";

export class LocalStorageProvider implements ILocalStorageProvider
{
    private isValidCostCode(code: IDefaultCostCodes): boolean
    {
        return Boolean(
            code?.CostCodeID?.trim()?.length>0 &&
            code?.CostCodeName?.trim()?.length>0 &&
            code?.CostCodeApportionment?.trim()?.length>0 &&
            typeof code?.CostCodeDescription === 'string'
        );
    }

    public setUserPreferences(value: IUserPreferences): void
    {
        let defaultBookingCostCodes = value.DefaultBookingCostCodes;
        if (!Array.isArray(defaultBookingCostCodes))
        {
            defaultBookingCostCodes = [];
        } 
        else
        {
            defaultBookingCostCodes = defaultBookingCostCodes.filter(defaultBookingCostCode=>this.isValidCostCode(defaultBookingCostCode)).map(costCode => ({
                CostCodeID: costCode.CostCodeID,
                CostCodeName: costCode.CostCodeName,
                CostCodeDescription: costCode.CostCodeDescription ?? '',
                CostCodeApportionment: costCode.CostCodeApportionment
            }));
        }
        value.DefaultBookingCostCodes = defaultBookingCostCodes;

        let defaultCateringCostCodes = value.DefaultCateringCostCodes;
        if (!Array.isArray(defaultCateringCostCodes))
        {
            defaultCateringCostCodes = [];
        } 
        else
        {
            defaultCateringCostCodes = defaultCateringCostCodes.filter(this.isValidCostCode).map(costCode => ({
                CostCodeID: costCode.CostCodeID,
                CostCodeName: costCode.CostCodeName,
                CostCodeDescription: costCode.CostCodeDescription ?? '',
                CostCodeApportionment: costCode.CostCodeApportionment
            }));
        }
        value.DefaultCateringCostCodes = defaultCateringCostCodes;
        localStorage.setItem("userPreferences", JSON.stringify(value));
    }

    public getUserPreferences(): IUserPreferences
    {
        return JSON.parse(localStorage.getItem("userPreferences") || "{}")
    }

    public setUserDetails(value: IUserDetails): void
    {
        const email = value.email ? value.email.toLowerCase() : '';
        const userDetails = { ...value, email };
        localStorage["user"] = JSON.stringify(userDetails);
    }

    public getUserDetails(): IUserDetails
    {
        return JSON.parse(localStorage.getItem("user") || "{}");
    }

    public setToken(value: string): void
    {
        localStorage.setItem("token", value);
    }

    public getToken(): string
    {
        return localStorage.getItem("token") ?? ""
    }

    public setVersion(value: number): void
    {
        localStorage["version"] = value;
    }

    public getVersion(): number
    {
        return localStorage["version"] ?? 0;
    }

    public setHubVersion(value: string): void
    {
        localStorage["hubVersion"] = value;
    }

    public getHubVersion(): string
    {
        return localStorage["hubVersion"] ?? "";
    }

    public setNodeData(value: IRootNode): void
    {
        localStorage["nodeData"] = JSON.stringify(value);
    }

    public getNodeData(): IRootNode
    {
        return JSON.parse(localStorage["nodeData"] || "{}");
    };

    public setIbssRights(value: IUserRights): void
    {
        localStorage.setItem("ibssRightList", JSON.stringify(value));
    }

    public getIbssRightList(): IUserRights
    {
        return JSON.parse(localStorage.getItem("ibssRightList") || "{}");
    }

    public clear(): void
    {
        const version = this.getHubVersion();
        localStorage.clear();
        this.setHubVersion(version);
    }

    public setUserPolicy(value: boolean): void
    {
        localStorage.setItem("userPolicyAccepted", JSON.stringify(value));
    }

    public getUserPolicy(): boolean
    {
        return JSON.parse(localStorage.getItem("userPolicyAccepted") || "false")
    }
}

export interface ILocalStorageProvider
{
    setUserPreferences(value: IUserPreferences): void;
    getUserPreferences(): IUserPreferences;
    setUserDetails(value: IUserDetails): void;
    getUserDetails(): IUserDetails;
    setToken(value: string): void;
    getToken(): string;
    setVersion(value: number): void;
    getVersion(): number;
    setHubVersion(value: string): void;
    getHubVersion(): string;
    setNodeData(value: IRootNode): void;
    getNodeData(): IRootNode;
    setIbssRights(value: IUserRights): void;
    getIbssRightList(): IUserRights;
    clear(): void;
    setUserPolicy(value: boolean): void;
    getUserPolicy(): boolean;
}
